// hooks/market/useUpdateProduct.ts
//
// Gère les actions de modification ET de suppression d'un produit, pour
// l'écran d'édition complète app/(tabs)/market/gerer-produit/[productId].tsx.
//
// - updateProduct(productId, fields) : met à jour un ou plusieurs champs
//   d'un produit. Utilisé pour toute la fiche produit (description, prix,
//   stock, images, boutons d'action, SEO…) ainsi que pour enregistrer
//   cover_image_url/image_2_url/image_3_url après un upload via
//   ImagePickerField.
// - deleteProduct(productId) : supprime définitivement un produit.
//   Action irréversible, à confirmer côté UI avant l'appel (simple
//   pop-up Confirmer/Annuler suffisant ici — contrairement à la
//   suppression d'une boutique, un produit seul est moins critique et ne
//   supprime rien d'autre en cascade).
//
// Les deux actions ont leurs propres états de chargement/erreur
// indépendants (saving/error vs deleting/deleteError), pour que l'UI
// puisse distinguer "en train d'enregistrer" de "en train de supprimer"
// sans ambiguïté.
//
// Appelle le vrai backend :
//   POST {API_BASE_URL}/market   action: 'update-product', { product_id, ...fields }
//   POST {API_BASE_URL}/market   action: 'delete-product', { product_id }
// (voir backend/handleMarket.ts → updateProduct / deleteProduct)
//
// ProductUpdateFields reprend EXACTEMENT la liste blanche ALLOWED_FIELDS
// du handler updateProduct (patch V9) — tous les champs modifiables par
// le owner d'un produit via cette action. Volontairement absents (voir
// handleMarket.ts pour le détail) :
//   - slug             → dérivé automatiquement, verrouillé après création
//   - sku              → non exposé à la modification
//   - disabled_reason   → réservé à l'admin
//   - id, shop_id, created_at, updated_at → identité / colonnes système
//
// ⚠️ Rappel (voir handleMarket.ts → deleteProduct) : aucune policy RLS
// DELETE dédiée n'existe côté market_products pour le owner à ce jour
// (seul l'admin est couvert par market_products_admin_all). Le check
// d'ownership applicatif côté backend est donc la seule barrière — à
// garder en tête si une policy market_products_owner_delete doit être
// ajoutée plus tard en base.
//
// Tous les champs sont optionnels ici : l'appelant n'envoie que ce qu'il
// veut réellement modifier (le backend ignore les clés absentes du body).

import { useState, useCallback } from 'react';
import { API_BASE_URL } from '@/lib/api';
import { getValidToken } from '@/lib/auth';

export type ProductStatus = 'draft' | 'published' | 'disabled' | 'archived';
export type ProductCondition = 'new' | 'used' | 'refurbished';

export interface ProductUpdateFields {
  // Identité / description
  name?: string;
  description_short?: string;
  description?: string;
  brand?: string;
  condition?: ProductCondition;

  // Images
  cover_image_url?: string | null;
  image_2_url?: string | null;
  image_3_url?: string | null;

  // Prix & stock
  price?: number;
  promo_price?: number | null;
  currency_code?: string;
  stock_quantity?: number;

  // Catégorisation & mise en avant
  category_id?: string;
  is_featured?: boolean;

  // Bouton WhatsApp (produit)
  show_whatsapp_business_button?: boolean;
  whatsapp_business_phone_override?: string | null;

  // Autres boutons d'action
  show_platform_chat_button?: boolean;
  show_buy_button?: boolean;
  buy_url?: string | null;
  show_learn_more_button?: boolean;
  learn_more_url?: string | null;
  show_call_button?: boolean;
  call_phone?: string | null;

  // SEO / partage social
  seo_title?: string | null;
  seo_description?: string | null;
  og_image_url?: string | null;

  // Données libres additionnelles
  extra_data?: Record<string, any>;

  // Statut de publication
  status?: ProductStatus;
}

export function useUpdateProduct() {
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState<string | null>(null);

  const [deleting, setDeleting]       = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const updateProduct = useCallback(async (
    productId: string,
    fields: ProductUpdateFields
  ): Promise<boolean> => {
    setSaving(true);
    setError(null);

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        return false;
      }

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'update-product',
          access_token: token,
          product_id:   productId,
          ...fields,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Impossible de mettre à jour le produit');
        return false;
      }

      return true;
    } catch (err) {
      console.error('[useUpdateProduct][updateProduct]', err);
      setError('network_error');
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  // Suppression définitive d'un produit (owner only, côté backend) —
  // irréversible, à confirmer explicitement côté UI (simple pop-up
  // Confirmer/Annuler) avant d'appeler cette fonction.
  const deleteProduct = useCallback(async (
    productId: string
  ): Promise<boolean> => {
    setDeleting(true);
    setDeleteError(null);

    try {
      const token = await getValidToken();
      if (!token) {
        setDeleteError('session_expired');
        return false;
      }

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'delete-product',
          access_token: token,
          product_id:   productId,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setDeleteError(data.error || 'Impossible de supprimer le produit');
        return false;
      }

      return true;
    } catch (err) {
      console.error('[useUpdateProduct][deleteProduct]', err);
      setDeleteError('network_error');
      return false;
    } finally {
      setDeleting(false);
    }
  }, []);

  return {
    updateProduct, saving, error,
    deleteProduct, deleting, deleteError,
  };
}
